import { api, apiUrl } from './client';
import type {
  Analysis,
  AnalysisOptions,
  AnalysisPage,
  AnalysisProgress,
  AnalysisStatus,
  CreateAnalysisResult,
  DesignDocument,
  DetectedComponent,
  ExportFormat,
  Paginated,
  ScreenshotView,
  StylesResponse,
} from '@/types';

/** Analysis endpoints. One function per route, no state. */

export interface ListAnalysesParams {
  projectId?: string;
  status?: AnalysisStatus;
  limit?: number;
  offset?: number;
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

export const analysesApi = {
  create: (input: { url: string; options?: Partial<AnalysisOptions>; projectId?: string | null }) =>
    api.post<CreateAnalysisResult>('/api/analyses', input),

  list: (params: ListAnalysesParams = {}) =>
    api.get<Paginated<Analysis>>(`/api/analyses${query({ ...params })}`),

  get: (id: string) => api.get<Analysis>(`/api/analyses/${id}`),

  progress: (id: string) => api.get<AnalysisProgress>(`/api/analyses/${id}/progress`),

  cancel: (id: string) => api.post<Analysis>(`/api/analyses/${id}/cancel`),

  retry: (id: string) => api.post<Analysis>(`/api/analyses/${id}/retry`),

  duplicate: (id: string) => api.post<CreateAnalysisResult>(`/api/analyses/${id}/duplicate`),

  remove: (id: string) => api.delete<void>(`/api/analyses/${id}`),

  screenshots: (id: string) =>
    api.get<{ items: ScreenshotView[] }>(`/api/analyses/${id}/screenshots`),

  pages: (id: string) => api.get<{ items: AnalysisPage[] }>(`/api/analyses/${id}/pages`),

  styles: (id: string) => api.get<StylesResponse>(`/api/analyses/${id}/styles`),

  components: (id: string) =>
    api.get<{ items: DetectedComponent[] }>(`/api/analyses/${id}/components`),

  designDocument: (id: string, version?: number) =>
    api.get<DesignDocument>(`/api/analyses/${id}/design-md${query({ version })}`),

  saveDesignDocument: (id: string, content: string) =>
    api.put<DesignDocument>(`/api/analyses/${id}/design-md`, { content }),

  resetDesignDocument: (id: string) =>
    api.post<DesignDocument>(`/api/analyses/${id}/design-md/reset`),

  exportAs: (id: string, format: ExportFormat) =>
    api.get<string>(`/api/analyses/${id}/export${query({ format })}`, { raw: true }),

  /** URL for a browser-initiated download, so the file name comes from the server. */
  downloadUrl: (id: string, format: ExportFormat) =>
    apiUrl(`/api/analyses/${id}/export${query({ format, download: 'true' })}`),

  /** URL of the Server-Sent Events progress stream. */
  streamUrl: (id: string) => apiUrl(`/api/analyses/${id}/stream`),
};
